import { useFormContext, Controller, type FieldValues } from 'react-hook-form';
import { Checkbox, Flex, Select } from 'pink-lava-ui';
import type { roundedType } from 'pink-lava-ui/types';
import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'es-toolkit';
import { type SelectOptionsParams } from '@dc-trans/data-layer';
import { useSelectOptionsStore } from '@/stores/selectOptionsStore';

export interface SelectOption {
  label: string;
  value: string | number;
  obj?: any;
  disabled?: boolean;
}

interface SelectInputProps<T extends FieldValues> {
  name: (keyof T & string) | '';
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  multiple?: boolean;
  allowSearch?: boolean;
  allowClear?: boolean;
  loading?: boolean;
  showErrorMessage?: boolean;
  onChange?: (_value: any, _option: any, _fieldOnChange: (_value: any) => void) => void;
  onChangeCallback?: (_value: any, _option: any, _setValue: (_name: keyof T & string, _value: any) => void) => void;

  onSearch?: (_val: any, _id: any) => void;
  validate?: (_value: any) => boolean | string;
  options?: ({ subLabel?: string } & SelectOption)[];
  // API-based options props
  apiUrl?: string;
  apiParams?: SelectOptionsParams;
  renderOption?: (_item: any) => { subLabel?: string } & SelectOption;
  size?: 'small' | 'middle' | 'large';
  className?: string;
  rounded?: roundedType;
  mode?: 'multiple' | 'tags';
  variant?: 'filled' | 'borderless' | 'underlined' | 'outlined';
  searchKey?: string;
  cacheKey?: string;
  info?: string;
}

export const SelectInput = <T extends FieldValues>({
  name,
  label,
  placeholder = 'Select option...',
  disabled = false,
  required = false,
  multiple = false,
  allowSearch = false,
  allowClear = true,
  loading = false,
  showErrorMessage = true,
  onChange,
  onChangeCallback,
  // onSearch,
  validate,
  options,
  apiUrl,
  apiParams,
  renderOption,
  size = 'middle',
  className = '',
  rounded,
  mode,
  variant,
  searchKey,
  cacheKey,
  info,
}: SelectInputProps<T>) => {
  const { control, setValue } = useFormContext();
  const [searchTerm, setSearchTerm] = useState('');

  // Use Zustand store instead of React Query
  const { fetchOptions, loading: loadingState, options: optionsState } = useSelectOptionsStore();

  // Create cache key for this specific field

  // Get options from store
  const apiOptions = useMemo(() => {
    if (!apiUrl) return [];
    const key = cacheKey ?? apiUrl;
    return Array.isArray(optionsState[key]) ? optionsState[key] : [];
  }, [apiUrl, optionsState, cacheKey]);

  // Determine final options (API or static)
  const selectOptions = useMemo(() => {
    return (apiUrl ? apiOptions : options || []).map(item => ({
      ...item,
      obj: item,
    }));
  }, [apiUrl, apiOptions, options]);

  // Fetch options when needed
  useEffect(() => {
    if (apiUrl && renderOption) {
      fetchOptions(apiUrl, { ...apiParams, ...(searchKey ? { [searchKey]: searchTerm } : {}) }, renderOption, cacheKey);
    }
  }, [apiUrl, apiParams, searchTerm, renderOption, fetchOptions, searchKey, cacheKey]);

  // Debounced search
  const debouncedSearch = useMemo(() => {
    return debounce((value: string) => {
      setSearchTerm(value);
    }, 500);
  }, []);

  const isLoadingData = apiUrl ? loadingState[cacheKey ?? apiUrl] : loading;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const selectClassName = `w-full ${rounded === 'large' ? 'rounded-full' : rounded ? 'rounded-lg' : ''} ${className}`;

  const isMultiple = mode === 'multiple' || !!multiple;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? `${label || 'This field'} is required` : false,
        validate: validate,
      }}
      render={({ field, fieldState: { error } }) => {
        return (
          <div className="w-full">
            {label && (
              <label className="block text-sm font-medium text-gray-700 mb-1 font-nunito">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            <Select
              variant={variant}
              value={field.value}
              placeholder={placeholder}
              disabled={disabled}
              mode={isMultiple ? 'multiple' : undefined}
              allowClear={allowClear}
              onClear={() => {
                setSearchTerm('');
              }}
              multiple={isMultiple}
              loading={isLoadingData}
              onChange={(value, option) => {
                const finalValue = value === undefined ? null : value;
                if (onChange) {
                  // Scenario 1: onChange overrides default behavior
                  onChange(finalValue, option, field.onChange);
                } else {
                  // Default behavior: update form field
                  field.onChange(finalValue);
                  // Scenario 2: onChangeCallback executes alongside default behavior
                  onChangeCallback?.(finalValue, option, setValue);
                }
              }}
              optionRender={item => {
                const value: string[] =
                  (isMultiple && typeof field.value === 'string' ? [field.value] : field.value) || [];
                return (
                  <Flex align="center" gap={8}>
                    {isMultiple && <Checkbox checked={value.includes(item.value as string)} size="small" />}
                    <div>
                      {item.label}
                      {item.data.subLabel && <div className="text-gray-400 text-xs">{item.data.subLabel}</div>}
                    </div>
                  </Flex>
                );
              }}
              onSearch={allowSearch || apiUrl ? debouncedSearch : undefined}
              options={selectOptions}
              size={size}
              className={`${selectClassName} ${error ? 'border-red-500!' : ''}`}
              filterOption={
                !(apiUrl && renderOption)
                  ? (input, option: any) => {
                      return (option?.obj?.label ?? '').toLowerCase().includes(input.toLowerCase());
                    }
                  : false
              }
              rounded={rounded}
              autoClearSearchValue={true}
              allowSearch={allowSearch || !!apiUrl}
              searchValue={searchTerm}
              maxTagCount={0}
              maxTagPlaceholder={ommitedValues => `${ommitedValues.length} dipilih`}
            />
            {info && <span className="text-gray-500 text-xs mt-1 block">{info}</span>}
            {showErrorMessage && error && <span className="text-red-500 text-xs mt-1 block">{error.message}</span>}
          </div>
        );
      }}
    />
  );
};
